import { Vector2 } from "../../../common/vector2.js";
import { AdventureBoxReward } from "../../../adventure/adventure_box_rewards.js";
import { ImgID } from "../../../image_loader/img_id.js";
import { Easing } from "../../../transforms/transform_base.js";
import { TransformScale } from "../../../transforms/transform_scale.js";
import { TransformStraightMove } from "../../../transforms/transform_straight_move.js";
import { ColorScheme } from "../../common_ui_elements/color_scheme.js";
import { TextStyleScheme } from "../../common_ui_elements/text_style_scheme.js";
import { UIElement } from "../../common_ui_elements/ui_element.js";
import { UICanvasRect } from "../../common_ui_elements/ui_canvas_rect.js";
import { Game } from "../../../game.js";
import { UIImage } from "../../common_ui_elements/ui_image.js";
import { MyTextAlign, MyTextVerticalAlign, UITextElement } from "../../common_ui_elements/ui_text_element.js";
import { AdventureBoxOpenPopup } from "../../screens/adventure_screen/adventure_box_open_popup.js";
import { AdventureBoxArt } from "../../screens/adventure_screen/adventure_box_art.js";
import { AbstractOverlay } from "../abstract_overlay.js";
import { OverlayId } from "../overlay_ids.js";
import { OverlayManager } from "../overlay_manager.js";

export interface FeatureEndedOverlayParams
{
  rewards: AdventureBoxReward[];
  top_part_image_id?: ImgID;
  top_part_width?: number;
  top_part_height?: number;
  title_localization_key?: string;
  title_y?: number;
  title_width?: number;
  title_height?: number;
  title_font_size?: number;
  on_closed?: () => void;
}

export class FeatureEndedOverlay extends AbstractOverlay
{
  private static readonly TOP_PART_CHEST_OVERLAP_RATIO = 0.60;

  private readonly design_offset_x: number;
  private readonly header_offset_y: number;
  private readonly overlay_params: FeatureEndedOverlayParams;
  private phase: "waiting" | "opening" = "waiting";
  private backdrop!: UICanvasRect;
  private top_part!: UIImage;
  private title_layers: UIElement[] = [];
  private top_text!: UITextElement;
  private bottom_text!: UITextElement;
  private start_box_opening: () => void = () => {};
  private close_notified = false;

  constructor(
    ctx: CanvasRenderingContext2D,
    x: number,
    y: number,
    width: number,
    height: number,
    params: FeatureEndedOverlayParams,
    overlay_id: OverlayId
  )
  {
    super( ctx, x, y, width, height, overlay_id, params, false );
    this.design_offset_x = ( this.rect.getW() - LayoutScaler.widthFromDesign( 1080 ) ) / 2;
    this.header_offset_y = this.rect.getH() / 2 - LayoutScaler.yFromDesign( 960 );
    this.overlay_params = params;
    this.createElements( params.rewards );
  }

  private xFromDesign( x: number ): number
  {
    return this.design_offset_x + LayoutScaler.xFromDesign( x );
  }

  private centeredXFromDesign( width: number ): number
  {
    return this.xFromDesign( ( 1080 - width ) / 2 );
  }

  private createElements( rewards: AdventureBoxReward[] ): void
  {
    // Own dim instead of the CSS #overlayBackground, so it can leave on the
    // claim tap together with the chest (the CSS one stays until close).
    // Spans the physical canvas: the overlay rect stops at the safe areas.
    this.backdrop = new UICanvasRect(
      this.ctx,
      -Game.safeAreaLeft(),
      -Game.safeAreaTop(),
      this.rect.getW() + Game.safeAreaLeft() + Game.safeAreaRight(),
      this.rect.getH() + Game.safeAreaTop() + Game.safeAreaBottom(),
      "rgba(0, 0, 0, 0.8)",
    );
    this.backdrop.setOnClick( () => {} );
    this.addChild( this.backdrop );

    const top_part_width = this.overlay_params.top_part_width ?? 825.1;
    const top_part_height = this.overlay_params.top_part_height ?? 605.68;
    this.top_part = new UIImage(
      this.ctx,
      this.centeredXFromDesign( top_part_width ),
      this.headerYFromDesign( 132 ),
      LayoutScaler.widthFromDesign( top_part_width ),
      LayoutScaler.heightFromDesign( top_part_height ),
      this.overlay_params.top_part_image_id ?? ImgID.UI_AdventureEndedTopPart
    );
    this.addChild( this.top_part );

    this.createTitle();
    this.top_text = this.createText(
      "ADVENTURE.ENDED_OVERLAY.TOP_TEXT",
      ( 1080 - 820 ) / 2,
      725,
      820,
      53,
      60,
      false
    );

    this.bottom_text = this.createText(
      "ADVENTURE.ENDED_OVERLAY.BOTTOM_TEXT",
      ( 1080 - 750 ) / 2,
      1691,
      750,
      67,
      50,
      true,
      true
    );

    const closed_box_height = LayoutScaler.heightFromDesign(
      AdventureBoxArt.MAIN_H + AdventureBoxArt.CLOSE_OFFSET_Y
    );
    const popup = new AdventureBoxOpenPopup( this.ctx, this );
    this.start_box_opening = popup.open( rewards, {
      center_x: this.rect.getW() / 2,
      // AdventureBoxArt expects the bottom edge, not the visual center.
      center_y: this.rect.getH() / 2 + closed_box_height / 2,
      wait_for_start: true,
      show_backdrop: false,
      release_before_coins_land: true,
      on_claim: ( start_time, fade_ms ) =>
      {
        this.backdrop.fadeOut( fade_ms, start_time, Easing.EaseInQuad );
        this.top_part.fadeOut( fade_ms, start_time, Easing.EaseInQuad );
      },
      on_rewards_rise_started: ( start_time, duration_ms, target_box_top_y ) =>
        this.animateTopPartDuringRewardsRise( start_time, duration_ms, target_box_top_y ),
      on_closed: () => OverlayManager.closeOverlay(),
    } );
  }

  private createTitle(): void
  {
    const title_width = this.overlay_params.title_width ?? 658;
    const x = this.centeredXFromDesign( title_width );
    const y = this.headerYFromDesign( this.overlay_params.title_y ?? 576.08 );
    const w = LayoutScaler.widthFromDesign( title_width );
    const h = LayoutScaler.heightFromDesign( this.overlay_params.title_height ?? 125 );
    const common = {
      localization_key: this.overlay_params.title_localization_key
        ?? "ADVENTURE.ENDED_OVERLAY.TITLE_TEXT",
      font_size: LayoutScaler.textSizeFromDesign( this.overlay_params.title_font_size ?? 90 ),
      text_align: MyTextAlign.Center,
      vertical_align: MyTextVerticalAlign.Center,
      text_style: TextStyleScheme.geologica,
      text_color: ColorScheme.white,
      font_weight: 900,
    };

    const outer_outline = new UITextElement( this.ctx, x, y + LayoutScaler.yFromDesign( 3 ), w, h, {
      ...common,
      outline_width: LayoutScaler.textBorderSizeFromDesign( 24 ),
      outline_color: "#823300",
    } );
    const inner_outline = new UITextElement( this.ctx, x, y, w, h, {
      ...common,
      outline_width: LayoutScaler.textBorderSizeFromDesign( 14 ),
      outline_color: "#FD6300",
    } );
    const title = new UITextElement( this.ctx, x, y, w, h, {
      ...common,
      shadow_blur: 0,
      shadow_offset_x: 0,
      shadow_offset_y: LayoutScaler.yFromDesign( 3 ),
      shadow_color: "#100654",
    } );

    this.title_layers = [ outer_outline, inner_outline, title ];
    for ( const layer of this.title_layers )
      this.addChild( layer );
  }

  private animateHeaderOut( now: number ): void
  {
    const text_duration_ms = 120;

    for ( const element of [ ...this.title_layers, this.top_text ] )
      element.fadeOut( text_duration_ms, now, Easing.EaseInQuad );
  }

  private animateTopPartDuringRewardsRise( start_time: number, duration_ms: number, target_box_top_y: number ): void
  {
    const target_scale = 0.9;
    const open_box_height = LayoutScaler.heightFromDesign(
      AdventureBoxArt.MAIN_H + AdventureBoxArt.OPEN_OFFSET_Y
    );
    const overlap = open_box_height * FeatureEndedOverlay.TOP_PART_CHEST_OVERLAP_RATIO;
    const art_h = this.top_part.getRect().getH();
    // TransformScale scales around the image center. At 0.9 scale the visible
    // bottom is at layoutY + 0.95 * height. Position it relative to the
    // risen chest so the chest consistently overlaps the art on every screen.
    const target_art_y = Math.max(
      0,
      target_box_top_y + overlap - art_h * ( 1 + target_scale ) / 2
    );

    this.top_part.addTransform( new TransformStraightMove(
      this.top_part,
      true,
      null,
      new Vector2( this.top_part.pixelPosImportant().x, target_art_y ),
      start_time,
      duration_ms,
      { easing: Easing.EaseOutQuad }
    ) );
    this.top_part.addTransform( new TransformScale(
      this.top_part,
      true,
      null,
      new Vector2( target_scale, target_scale ),
      start_time,
      duration_ms,
      { easing: Easing.EaseOutQuad }
    ) );
  }

  private headerYFromDesign( y: number ): number
  {
    return this.header_offset_y + LayoutScaler.yFromDesign( y );
  }

  private createText(
    localization_key: string,
    x: number,
    y: number,
    w: number,
    h: number,
    font_size: number,
    auto_wrap: boolean = true,
    anchor_to_bottom: boolean = false
  ): UITextElement
  {
    const text_y = anchor_to_bottom
      ? this.rect.getH() - LayoutScaler.heightFromDesign( 1920 - y )
      : this.headerYFromDesign( y );
    const text = new UITextElement(
      this.ctx,
      this.xFromDesign( x ),
      text_y,
      LayoutScaler.widthFromDesign( w ),
      LayoutScaler.heightFromDesign( h ),
      {
        localization_key,
        auto_wrap,
        font_size: LayoutScaler.textSizeFromDesign( font_size ),
        text_align: MyTextAlign.Center,
        vertical_align: MyTextVerticalAlign.Center,
        text_style: TextStyleScheme.geologica,
        text_color: ColorScheme.white,
        font_weight: 800,
        shadow_blur: 0,
        shadow_offset_y: LayoutScaler.yFromDesign( 3 ),
        shadow_color: "#100654",
      }
    );
    this.addChild( text );
    return text;
  }

  public override onTouchEnd( p: Vector2, now: number ): void
  {
    if ( this.phase === "waiting" )
    {
      this.phase = "opening";
      this.bottom_text.fadeOut( 100, now, Easing.EaseOutQuad );
      this.animateHeaderOut( now );
      this.start_box_opening();
      return;
    }

    super.onTouchEnd( p, now );
  }

  public override getViewportBackground(): string | null
  {
    return "transparent";
  }

  public render(): void
  {
    if ( !this.shouldBeRendered() ) return;
    this.startRender();
    this.finishRender();
  }

  public override onOverlayWillClose(): void
  {
    if ( this.close_notified ) return;
    this.close_notified = true;
    this.overlay_params.on_closed?.();
  }

  public name(): string
  {
    return this.getId() === OverlayId.DAILY_TASKS_ENDED_OVERLAY
      ? "DailyTasksEndedOverlay"
      : "AdventureEndedOverlay";
  }
}
